import { useState } from 'react';
import useLocalStorage from '@/hooks/useLocalStorage';
import { defaultCategories, type AdminCategory } from '@/mocks/adminData';

const emptyCategory: AdminCategory = {
  id: '',
  name: '',
  shortDescription: '',
  image: '',
  active: true,
};

function generateId(list: AdminCategory[]): string {
  const max = list.reduce((acc, c) => {
    const n = parseInt(c.id.replace('cat-', ''), 10);
    return n > acc ? n : acc;
  }, 0);
  return `cat-${max + 1}`;
}

function CategoriesPage() {
  const [categories, setCategories] = useLocalStorage<AdminCategory[]>('admin_categories', defaultCategories);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<AdminCategory | null>(null);
  const [form, setForm] = useState<AdminCategory>(emptyCategory);
  const [deleteTarget, setDeleteTarget] = useState<AdminCategory | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyCategory, id: generateId(categories) });
    setShowModal(true);
  };

  const openEdit = (cat: AdminCategory) => {
    setEditing(cat);
    setForm({ ...cat });
    setShowModal(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    if (editing) {
      setCategories(categories.map((c) => (c.id === form.id ? form : c)));
    } else {
      setCategories([...categories, form]);
    }
    setShowModal(false);
    setEditing(null);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setCategories(categories.filter((c) => c.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  const handleToggle = (id: string) => {
    setCategories(categories.map((c) => (c.id === id ? { ...c, active: !c.active } : c)));
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Flash */}
      {savedFlash && (
        <div className="fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium shadow-sm animate-in">
          <i className="ri-check-line mr-1.5" />
          Alterações salvas com sucesso.
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Categorias</h1>
          <p className="text-sm text-foreground-500 mt-1">Gerencie as categorias do catálogo.</p>
        </div>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Nova categoria
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        {categories.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-price-tag-3-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-500 mb-1">Nenhuma categoria cadastrada</p>
            <button type="button" onClick={openNew} className="text-xs text-primary-600 hover:text-primary-700 font-medium">
              Criar primeira categoria
            </button>
          </div>
        ) : (
          <div className="divide-y divide-background-100">
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-4 px-5 py-4 hover:bg-background-50 transition-colors">
                <img src={cat.image} alt={cat.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground-800">{cat.name}</p>
                  <p className="text-xs text-foreground-500 truncate">{cat.shortDescription}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle(cat.id)}
                  className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors whitespace-nowrap ${
                    cat.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {cat.active ? 'Ativa' : 'Inativa'}
                </button>
                <button
                  type="button"
                  onClick={() => openEdit(cat)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-500 hover:text-foreground-700 transition-colors"
                >
                  <i className="ri-edit-line text-sm" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(cat)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
              {editing ? 'Editar categoria' : 'Nova categoria'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Nome da categoria</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="Ex: Bebê"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Descrição curta</label>
                <input
                  type="text"
                  value={form.shortDescription}
                  onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="Breve descrição"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">URL da imagem</label>
                <input
                  type="text"
                  value={form.image}
                  onChange={(e) => setForm({ ...form, image: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="https://..."
                />
                {form.image && (
                  <img src={form.image} alt="Preview" className="mt-2 w-20 h-20 rounded-lg object-cover border border-background-200" />
                )}
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400"
                  />
                  <span className="text-sm text-foreground-700">Categoria ativa</span>
                </label>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-background-100">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2.5 rounded-full text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors whitespace-nowrap"
              >
                Salvar alterações
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-sm p-6">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-delete-bin-line text-lg text-red-500" />
            </div>
            <p className="text-sm text-foreground-700 text-center mb-1">Tem certeza que deseja excluir?</p>
            <p className="text-xs text-foreground-500 text-center mb-5">{deleteTarget.name}</p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoriesPage;